import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../redux/api";
import "./OutgoingInvoiceEmails.css";
const LABELS = {
  pending: "Waiting to send",
  retry: "Retry scheduled",
  sending: "Sending",
  accepted: "Accepted",
  delivered: "Delivered",
  delivery_delayed: "Delayed",
  failed: "Failed",
  bounced: "Bounced",
  complained: "Spam complaint",
  suppressed: "Blocked",
  review: "Needs review",
  cancelled: "Cancelled",
  staging_suppressed: "Not sent — staging",
};
function Preview({ data, close }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current.showModal();
  }, []);
  return (
    <dialog
      className="outgoing-email-modal"
      ref={ref}
      onCancel={close}
      onClose={close}
    >
      <header>
        <h3>Email preview</h3>
        <button type="button" aria-label="Close" onClick={close}>
          ×
        </button>
      </header>
      <div className="outgoing-email-envelope">
        <p>
          <strong>From:</strong> {data.from}
        </p>
        <p>
          <strong>To:</strong> {data.to.join(", ")}
        </p>
        <p>
          <strong>Subject:</strong> {data.subject}
        </p>
        {data.delivery_problem && <p role="status"><strong>Delivery:</strong> {data.delivery_problem}</p>}
      </div>
      <iframe
        title="Sent email body"
        sandbox=""
        srcDoc={
          "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; img-src https:; style-src 'unsafe-inline'\">" +
          data.html
        }
      />
    </dialog>
  );
}
export default function OutgoingInvoiceEmails() {
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0 }),
    [user, setUser] = useState(""),
    [status, setStatus] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [preview, setPreview] = useState(null);
  async function load(page = 1) {
    setBusy(true);
    setError("");
    try {
      const r = await axiosInstance.get("invoice_email/list", {
        params: {
          page,
          user_id: user || undefined,
          status: status || undefined,
        },
      });
      if (!r.data.status) throw new Error();
      setData(r.data.response);
    } catch {
      setError("Unable to load outgoing emails.");
    } finally {
      setBusy(false);
    }
  }
  async function open(id) {
    setError("");
    try {
      const r = await axiosInstance.get("invoice_email/preview/" + id);
      if (!r.data.status) throw new Error();
      setPreview(r.data.response);
    } catch {
      setError("Unable to open this email.");
    }
  }
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="outgoing-emails-page">
      <div className="category-wrapper">
        <header>
          <div>
            <h3>Outgoing invoice emails</h3>
            <p>Customer invoice emails and sender tests.</p>
          </div>
          <button type="button" disabled={busy} onClick={() => load(data.page)}>
            Refresh
          </button>
        </header>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
        >
          <label>
            User ID
            <input
              type="number"
              min="1"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="All users"
            />
          </label>
          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {Object.entries(LABELS).map(([key, label]) => (
                <option value={key} key={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={busy}>
            Apply
          </button>
        </form>
        {error && <p role="alert">{error}</p>}
        <div className="outgoing-emails-table">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>From</th>
                <th>To</th>
                <th>Time (Riyadh)</th>
                <th>Status</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.user}</strong>
                    <small>
                      ID {row.user_id}
                      {row.test ? " · Test" : ""}
                    </small>
                  </td>
                  <td>{row.from}</td>
                  <td>{row.to.join(", ")}</td>
                  <td>
                    {new Date(row.time).toLocaleString("en-GB", {
                      timeZone: "Asia/Riyadh",
                    })}
                  </td>
                  <td>
                    <span
                      className={"email-delivery-status status-" + row.status}
                    >
                      {LABELS[row.status] || row.status}
                    </span>
                  </td>
                  <td>
                    <button type="button" onClick={() => open(row.id)}>
                      Preview
                    </button>
                  </td>
                </tr>
              ))}
              {!data.items.length && (
                <tr>
                  <td colSpan="6">
                    {busy ? "Loading…" : "No outgoing invoice emails yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <footer>
          <button
            type="button"
            disabled={busy || data.page <= 1}
            onClick={() => load(data.page - 1)}
          >
            Previous
          </button>
          <span>
            {data.total} emails · Page {data.page} of {data.pages}
          </span>
          <button
            type="button"
            disabled={busy || data.page >= data.pages}
            onClick={() => load(data.page + 1)}
          >
            Next
          </button>
        </footer>
      </div>
      {preview && <Preview data={preview} close={() => setPreview(null)} />}
    </div>
  );
}
